/**
 * One import, while it is still running.
 *
 * The import is asynchronous by design: confirming a mapping answers with a
 * dataset in `processing`, and the counts arrive later. This panel is what a
 * screen shows in between — it polls `GET /datasets/:id/status` through
 * `useDatasetStatusPolling` and reports what has landed so far.
 *
 * Two deliberate refusals:
 *
 * - **No percentage.** The API publishes rows-read as it goes, but the total
 *   number of rows in the file is not known until the last one is read, so
 *   there is nothing honest to divide by. A bar that invents a denominator is
 *   worse than a count that is simply true.
 * - **No silent stall.** Polling gives up out loud after repeated failures, and
 *   this panel then says so and offers to start watching again, rather than
 *   leaving a spinner turning over a request that stopped happening.
 */
import { isDatasetInFlight, type DatasetStatusResponse } from '@wend/shared';
import { Button, Kicker, Spinner } from '@wend/ui';
import { RotateCw } from 'lucide-react';
import { useState } from 'react';

import { errorMessage, isApiError } from '../lib/api/errors';
import { formatCount } from '../lib/format';
import { useDatasetStatusPolling } from '../lib/use-dataset-status';
import { FormAlert } from './FormAlert';

export interface ImportProgressProps {
  /**
   * The status the caller already holds. A full `Dataset` satisfies this, so a
   * screen that has just started an import can hand over what the API returned
   * and the counts never flash through zero before the first poll lands.
   */
  initial: DatasetStatusResponse;
  /** Called once, when the import reaches `ready` or `failed`. */
  onSettled?: (status: DatasetStatusResponse) => void;
}

export function ImportProgress({ initial, onSettled }: ImportProgressProps) {
  // The polling hook keys its timer on the set of ids it was given and stops
  // for good once it has given up, so restarting it means a fresh mount. The
  // attempt counter is the only thing this outer component exists for.
  const [attempt, setAttempt] = useState(0);

  return (
    <Watcher
      key={attempt}
      initial={initial}
      {...(onSettled === undefined ? {} : { onSettled })}
      onRestart={() => {
        setAttempt((value) => value + 1);
      }}
    />
  );
}

function Watcher({
  initial,
  onSettled,
  onRestart,
}: ImportProgressProps & { onRestart: () => void }) {
  const { statuses, error } = useDatasetStatusPolling(
    isDatasetInFlight(initial.status) ? [initial.id] : [],
    onSettled,
  );

  const status = statuses[initial.id] ?? initial;
  const stalled = error !== null;

  return (
    <div className="flex flex-col gap-4 rounded-base border border-info/30 bg-info/[0.05] px-4 py-4">
      <div className="flex items-center gap-2.5">
        <Spinner size="md" label={null} />
        <p className="text-[0.95rem] text-text" role="status">
          Importing rows from this sheet.
        </p>
      </div>

      <dl className="flex flex-wrap gap-x-8 gap-y-3">
        <Counter label="Rows read" value={status.rowCount} />
        <Counter label="Imported" value={status.importedCount} />
        <Counter label="Rejected" value={status.rejectedCount} />
      </dl>

      <p className="text-[0.85rem] leading-relaxed text-muted">
        The total is only known once the last row has been read, so these counts climb rather than
        filling a bar. The import keeps running on the server if you leave this page.
      </p>

      {stalled && (
        <FormAlert
          tone="error"
          {...(isApiError(error) && error.requestId !== undefined
            ? { requestId: error.requestId }
            : {})}
        >
          {errorMessage(error)} Progress has stopped updating — the import itself may still be
          running.
        </FormAlert>
      )}

      {stalled && (
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leadingIcon={<RotateCw aria-hidden="true" />}
            onClick={onRestart}
          >
            Watch again
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * One climbing count. Sans with tabular figures since slice 66, not mono: these
 * digits change every second and a mono zero, dotted, reads as an 8 mid-change.
 * The label is the shared `Kicker` role, so it matches every other figure label
 * in the product rather than a hand-tuned copy of it.
 */
function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1">
      <Kicker as="dt">{label}</Kicker>
      <dd className="text-[1.25rem] font-semibold leading-none tracking-[-0.02em] tabular-nums text-text">
        {formatCount(value)}
      </dd>
    </div>
  );
}
